import { useEffect, useState } from "react";
import { BrowserRouter, NavLink, Route, Routes, Link, useLocation } from "react-router-dom";
import {
  FiArrowUpRight, FiMail, FiMapPin, FiMessageCircle, FiFacebook,
  FiInstagram, FiLinkedin, FiSend, FiCamera, FiPenTool, FiVideo,
  FiGrid, FiTrendingUp, FiEdit3
} from "react-icons/fi";
import { personalInfo, skills, tools, projects, experience } from "./data/portfolio";

const services = [
  { icon: <FiGrid/>, title: "Social Media Management", text: "Strategy, content planning, community engagement and growth across Facebook, Instagram, TikTok and YouTube." },
  { icon: <FiPenTool/>, title: "Graphic & Brand Design", text: "Logos, campaign artwork, promotional graphics and consistent visual systems built for modern brands." },
  { icon: <FiCamera/>, title: "Photography", text: "Events, portraits and products captured with a story-first approach and polished final retouching." },
  { icon: <FiVideo/>, title: "Video Editing", text: "Reels, shorts, brand videos and cinematic edits that hold attention and communicate clearly." },
  { icon: <FiEdit3/>, title: "Content Creation", text: "Captions, creative concepts, campaign ideas and conversion-focused social media content." },
  { icon: <FiTrendingUp/>, title: "Growth Strategy", text: "Audience-focused planning that connects creative work to engagement, trust and measurable results." },
];

const process = [
  ["01", "Discover", "Understand your brand, audience, goals and the problem we need to solve."],
  ["02", "Plan", "Build the creative direction, content structure and visual strategy."],
  ["03", "Create", "Design, shoot and edit the assets with attention to detail."],
  ["04", "Refine", "Polish the work through feedback, testing and thoughtful iteration."],
  ["05", "Deliver", "Prepare final assets for the right platforms and formats."],
];

const certs = [
  "Google Digital Marketing & E-commerce",
  "Google Analytics Foundations",
  "Social Media & Content Strategy",
];

function SectionTitle({ eyebrow, title, accent }) {
  return <div className="section-title"><span>{eyebrow}</span><h2>{title} {accent && <em>{accent}</em>}</h2></div>;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [["/","Home"],["/about","About"],["/services","Services"],["/work","Work"],["/process","Process"],["/contact","Contact"]];
  return (
    <nav className="topbar glass">
      <Link className="brand" to="/" onClick={()=>setMenuOpen(false)}><span className="brand-mark logo-mark"><img src="/logonew.png?v=6" alt="Sagar logo" /></span><span><b>Sagar Miah</b><small>Creative Designer & Photographer</small></span></Link>
      <div className={`nav-links ${menuOpen ? "open" : ""}`}>
        {links.map(([to,label]) => <NavLink key={to} to={to} onClick={()=>setMenuOpen(false)} className={({isActive})=>isActive?"active":""}>{label}</NavLink>)}
      </div>
      <Link className="talk-btn" to="/contact">Let's Talk <FiArrowUpRight/></Link>
      <button className="menu-btn" onClick={()=>setMenuOpen(!menuOpen)} aria-label="Toggle menu">{menuOpen ? "×" : "☰"}</button>
    </nav>
  );
}

function Footer() {
  return <footer className="site-footer glass-soft"><div><b>Ahamed Sagar</b><span>Creative Designer & Photographer</span></div><p>© 2026 Ahamed Sagar. All rights reserved.</p><Link to="/contact">Start a Project <FiArrowUpRight/></Link></footer>;
}

function Home() {
  return <>
    <main className="page glass">
      <section className="hero section-pad">
        <div className="hero-copy">
          <span className="eyebrow">HELLO, I'M</span>
          <h1>Ahamed Sagar</h1>
          <h2>Creative <span>Designer</span> & Photographer</h2>
          <p>I create visual experiences that help brands look sharper, communicate better and connect with the right audience across digital platforms.</p>
          <div className="hero-actions">
            <Link className="primary" to="/work">View My Work <FiArrowUpRight/></Link>
            <Link className="secondary" to="/contact">Let's Connect <FiMail/></Link>
          </div>
          <div className="trusted"><span>Focused on</span><b>Design</b><b>Photography</b><b>Social Media</b><b>Content</b></div>
        </div>
        <div className="hero-visual">
          <div className="portrait-card"><img src="/hero.png" alt="Ahamed Sagar"/></div>
          <div className="float-card exp"><strong>6+</strong><span>Years of Experience</span></div>
          <div className="float-card impact"><span>Creative Impact</span><strong>100%</strong><div className="mini-line"/></div>
        </div>
      </section>

      <section className="section-pad about-grid section-block">
        <div>
          <SectionTitle eyebrow="ABOUT ME" title="Designing with Purpose," accent="Telling Stories Visually"/>
          <p className="lead">{personalInfo.bio[0]}</p>
        </div>
        <div className="about-copy">
          <p>{personalInfo.bio[1]}</p><p>{personalInfo.bio[2]}</p>
          <Link className="text-link" to="/about">More About Me <FiArrowUpRight/></Link>
        </div>
        <div className="stats-row">
          {personalInfo.stats.map((s)=><div className="stat glass-soft" key={s.label}><strong>{s.number}{s.suffix}</strong><span>{s.label}</span></div>)}
        </div>
      </section>
    </main>
    <Footer/>
  </>;
}

function PageIntro({eyebrow,title,accent,text}) {
  return <section className="page-intro section-pad"><SectionTitle eyebrow={eyebrow} title={title} accent={accent}/><p>{text}</p></section>;
}

function AboutPage() {
  return <><main className="page glass"><PageIntro eyebrow="ABOUT ME" title="Creative Mind," accent="Visual Storyteller" text={personalInfo.bio[0]}/>
    <section className="section-pad inner-two section-block"><div className="about-photo glass-soft"><img src="/photo.png" alt="Ahamed Sagar"/></div><div className="about-long"><h3>Designing meaningful visual experiences</h3>{personalInfo.bio.map((p,i)=><p key={i}>{p}</p>)}<div className="stats-row compact">{personalInfo.stats.map((s)=><div className="stat glass-soft" key={s.label}><strong>{s.number}{s.suffix}</strong><span>{s.label}</span></div>)}</div></div></section>
    <section className="section-pad section-block experience-section"><SectionTitle eyebrow="EXPERIENCE" title="Where I've" accent="Worked"/><div className="experience-grid">{experience.map((e,i)=><article className="experience-card glass-soft" key={`${e.company}-${e.period}`}><div className="experience-card-top"><div className="experience-index">{String(i+1).padStart(2,"0")}</div><div className="experience-period"><span>{e.period}</span><b>{e.duration}</b></div>{e.current && <span className="current-badge">CURRENT</span>}</div><div className="experience-content"><h3>{e.role}</h3><h4>{e.company}</h4><p>{e.desc}</p></div><div className="tags experience-tags">{e.tags.map(t=><span key={t}>{t}</span>)}</div></article>)}</div></section>
    <section className="section-pad section-block"><SectionTitle eyebrow="CERTIFICATIONS" title="Learning" accent="& Growth"/><div className="cert-grid">{certs.map((c,i)=><div className="cert glass-soft" key={c}><span>0{i+1}</span><h3>{c}</h3><p>Continuous learning to combine creativity with strategy and measurable digital results.</p></div>)}</div></section>
  </main><Footer/></>;
}

function ServicesPage() {
  return <><main className="page glass"><PageIntro eyebrow="WHAT I DO" title="Services" accent="I Offer" text="Creative, content and growth services designed to help brands communicate clearly and look consistently professional."/>
    <section className="section-pad section-block"><div className="service-grid">{services.map((s)=><article className="service-card glass-soft" key={s.title}><div className="service-icon">{s.icon}</div><h3>{s.title}</h3><p>{s.text}</p><FiArrowUpRight className="corner-arrow"/></article>)}</div></section>
    <section className="section-pad section-block"><SectionTitle eyebrow="TOOLS & SKILLS" title="Technologies" accent="I Use"/><div className="tool-strip glass-soft">{tools.map(t=><div className="tool" key={t.name}><span>{t.icon}</span><small>{t.name}</small></div>)}</div><div className="skill-grid">{skills.map(s=><div className="skill-pill" key={s.name}><span>{s.name}</span><b>{s.level}%</b><i><u style={{width:`${s.level}%`}}/></i></div>)}</div></section>
  </main><Footer/></>;
}

function WorkPage() {
  return <><main className="page glass"><PageIntro eyebrow="PORTFOLIO" title="Selected" accent="Work" text="A selection of branding, photography, social media and video projects that reflect how I combine visual quality with business goals."/>
    <section className="section-pad section-block"><div className="project-grid work-grid">{projects.map((p,i)=><article className="project-card" key={p.id}><div className={`project-thumb p${(i%3)+1}`}><span>{p.emoji}</span><div className="project-mock"><i/><i/><i/></div></div><div className="project-info"><div><h3>{p.title}</h3><small>{p.tags.join(" · ")}</small><p>{p.desc}</p></div><FiArrowUpRight/></div></article>)}</div></section>
  </main><Footer/></>;
}

function ProcessPage() {
  return <><main className="page glass"><PageIntro eyebrow="MY PROCESS" title="How I" accent="Work" text="A simple, collaborative process that keeps every project focused, efficient and consistent from first idea to final delivery."/>
    <section className="section-pad section-block"><div className="process-grid process-page-grid">{process.map(([n,t,d])=><div className="process-card glass-soft" key={n}><span>{n}</span><h3>{t}</h3><p>{d}</p></div>)}</div></section>
    <section className="section-pad section-block process-note"><div><span className="eyebrow">COLLABORATION</span><h2>Clear communication at every stage.</h2></div><p>I keep the workflow transparent, share progress clearly and refine the creative direction based on practical feedback so the final result stays aligned with the goal.</p></section>
  </main><Footer/></>;
}

function ContactPage() {
  const [form,setForm]=useState({name:"",email:"",service:"",message:""});
  const [status,setStatus]=useState("idle");
  const socials=[
    {icon:<FiMail/>,label:"Email",href:`mailto:${personalInfo.contact.email}`},
    {icon:<FiMessageCircle/>,label:"WhatsApp",href:personalInfo.contact.whatsappLink},
    {icon:<FiFacebook/>,label:"Facebook",href:personalInfo.contact.facebookLink},
    {icon:<FiInstagram/>,label:"Instagram",href:personalInfo.contact.instagramLink},
    {icon:<FiLinkedin/>,label:"LinkedIn",href:personalInfo.contact.linkedinLink},
  ];
  const submit=async(e)=>{
    e.preventDefault();
    if(status==="sending") return;
    setStatus("sending");

    const endpoint = "https://formspree.io/f/mojynzlp";

    try{
      const res=await fetch(endpoint,{
        method:"POST",
        headers:{"Content-Type":"application/json",Accept:"application/json"},
        body:JSON.stringify({
          name:form.name.trim(),
          email:form.email.trim(),
          service:form.service || "Not specified",
          message:form.message.trim(),
          _subject:`New portfolio enquiry from ${form.name.trim()}`,
          _replyto:form.email.trim(),
          source:"sagarmiah.com portfolio"
        })
      });

      if(!res.ok){
        let details="";
        try{const data=await res.json();details=data?.errors?.map(x=>x.message).join(", ")||"";}catch{}
        throw new Error(details || "Unable to send message");
      }

      setStatus("success");
      setForm({name:"",email:"",service:"",message:""});
      window.setTimeout(()=>setStatus("idle"),7000);
    }catch(err){
      console.error("Contact form error:",err);
      setStatus("error");
    }
  };
  return <><main className="page glass"><PageIntro eyebrow="LET'S CONNECT" title="Let's Create" accent="Something Great" text="Tell me what you're building and what kind of creative support you need. I'll get back to you with the next steps."/>
    <section className="section-pad contact-grid section-block"><div className="contact-copy"><h2>Have a project in mind?<br/>Let's talk about <span>your idea.</span></h2><p>For design, photography, social media or content projects, send me a message with a little detail about the project.</p><div className="contact-list"><a href={`mailto:${personalInfo.contact.email}`}><FiMail/>{personalInfo.contact.email}</a><span><FiMapPin/>{personalInfo.location}</span></div><div className="socials">{socials.map(s=><a href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} key={s.label}>{s.icon}</a>)}</div></div>
    <form className="contact-form glass-soft" onSubmit={submit}><div className="form-row"><input required placeholder="Your Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/><input required type="email" placeholder="Your Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div><select value={form.service} onChange={e=>setForm({...form,service:e.target.value})}><option value="">Your Project</option>{services.map(s=><option key={s.title}>{s.title}</option>)}</select><textarea required placeholder="Your Message" value={form.message} onChange={e=>setForm({...form,message:e.target.value})}/><button className="primary submit" disabled={status==="sending"}>{status==="sending"?"Sending...":"Send Message"}<FiSend/></button>{status==="success"&&<p className="form-note success">Message sent successfully. I’ll get back to you soon.</p>}{status==="error"&&<p className="form-note error">Could not send right now. Please email me directly at md@sagarmiah.com.</p>}</form></section>
  </main><Footer/></>;
}

function ScrollTop(){const {pathname}=useLocation();useEffect(()=>{window.scrollTo(0,0);},[pathname]);return null;}

function AppShell(){return <div className="site-shell"><div className="ambient ambient-a"/><div className="ambient ambient-b"/><div className="ambient ambient-c"/><Header/><ScrollTop/><Routes><Route path="/" element={<Home/>}/><Route path="/about" element={<AboutPage/>}/><Route path="/services" element={<ServicesPage/>}/><Route path="/work" element={<WorkPage/>}/><Route path="/process" element={<ProcessPage/>}/><Route path="/contact" element={<ContactPage/>}/><Route path="*" element={<Home/>}/></Routes></div>}

export default function App(){return <BrowserRouter><AppShell/></BrowserRouter>}
